/* Shopify remains the authority for gift eligibility and discounts. */
(() => {
  'use strict';
  const dataElement = document.querySelector('[data-role="data-gift-products"]');
  if (!dataElement) return;
  const giftData = JSON.parse(dataElement.textContent);
  const guarantee = giftData.guarantee;
  const isGuarantee = item => guarantee && String(item.variant_id) === String(guarantee.variantId);
  const marker = '_isGiftProduct';
  const route = path => (window.Shopify?.routes?.root || '/') + path;
  const locks = new Set();
  let running = null;
  let syncing = false;
  let dirty = false;
  let failure = '';
  let reorderDetected = false;
  let latestCart = null;
  const isGift = item => Boolean(item.properties?.[marker]);
  const knownGiftIds = new Set([
    ...(giftData.subscriptionGiftProductList || []),
    ...(giftData.oneTimeGiftProductList || [])
  ].map(item => String(item.variantId)));
  const busy = () => syncing || locks.size > 0;

  function applyState() {
    const blocked = busy() || Boolean(failure);
    document.querySelectorAll('cart-drawer').forEach(drawer => {
      drawer.classList.toggle('nuoro-cart-busy', busy());
      drawer.setAttribute('aria-busy', String(busy()));
    });
    document.querySelectorAll('#CartDrawer-Checkout, #checkout[name="checkout"]').forEach(button => {
      const empty = latestCart ? latestCart.items.length === 0 : document.querySelector('cart-drawer')?.classList.contains('is-empty');
      button.disabled = blocked || Boolean(empty);
      if (busy()) button.setAttribute('aria-label', 'Mise à jour du panier en cours');
      else if (button.getAttribute('aria-label') === 'Mise à jour du panier en cours') button.removeAttribute('aria-label');
    });
    const region = document.querySelector('#CartDrawer-CartErrors') || document.querySelector('#cart-errors');
    if (!region) return;
    let message = region.querySelector('[data-nuoro-gift-error]');
    if (!failure) { message?.remove(); return; }
    if (message) return;
    message = document.createElement('div');
    message.dataset.nuoroGiftError = '';
    const text = document.createElement('p');
    text.textContent = failure;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Réessayer';
    retry.addEventListener('click', () => { sync().catch(() => {}); });
    message.append(text, retry);
    region.append(message);
  }

  function acquire() {
    const token = Symbol('cart mutation');
    locks.add(token);
    applyState();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      locks.delete(token);
      applyState();
    };
  }

  // This also covers a freshly rendered checkout before the observer runs.
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const checkout = target?.closest('[name="checkout"], a[href^="/checkout"], a[href*="/checkouts/"]');
    const mutation = target?.closest('cart-drawer quantity-input, cart-drawer cart-remove-button, cart-drawer cta-enable-subscription');
    if ((checkout && (busy() || failure)) || (mutation && busy())) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if (!(busy() || failure)) return;
    const form = event.target;
    if (form?.id === 'CartDrawer-Form' || form?.id === 'cart' || event.submitter?.name === 'checkout') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('keydown', event => {
    if (!busy() || !['Enter', ' '].includes(event.key)) return;
    if (event.target?.closest('cart-drawer quantity-input, cart-drawer cart-remove-button, cart-drawer cta-enable-subscription')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('change', event => {
    if (!busy() || !event.target?.closest('cart-drawer quantity-input')) return;
    event.target.value = event.target.getAttribute('value');
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  const observer = new MutationObserver(() => applyState());
  observer.observe(document.body, { childList: true, subtree: true });

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
    const response = await fetch(route(path), {
      signal: controller.signal,
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok || result.errors || result.status >= 400) {
      const error = new Error(result.description || result.message || 'Impossible de mettre à jour vos cadeaux.');
      error.status = response.status;
      throw error;
    }
    return result;
    } finally { clearTimeout(timeout); }
  }

  function giftProperties(id) {
    const properties = { [marker]: 'true' };
    if (String(id) === '52079106130252') properties.Shaker = 'Cadeau de bienvenue réservé à la première commande';
    if (String(id) === '57005710475596') properties['Sticks Découverte'] = 'Cadeau de bienvenue réservé à la première commande';
    if (guarantee && String(id) === String(guarantee.variantId)) {
      properties._nuoroGuarantee = 'true';
    }
    return properties;
  }

  async function reconcile() {
    // Re-read after each mutation: line keys and the server discounts can change.
    for (let pass = 0; pass < 6; pass++) {
      const cart = await request('cart.js');
      latestCart = cart;
      const reorderedGift = cart.items.some(item => knownGiftIds.has(String(item.variant_id)) && !isGift(item));
      if (reorderedGift) reorderDetected = true;
      const paid = cart.items.filter(item => !isGift(item) && !knownGiftIds.has(String(item.variant_id)) && !isGuarantee(item));
      const paidQuantity = paid.reduce((total, item) => total + item.quantity, 0);
      const configured = paid.some(item => item.selling_plan_allocation)
        ? giftData.subscriptionGiftProductList || []
        : giftData.oneTimeGiftProductList || [];
      const desired = new Map((paidQuantity && !reorderDetected ? configured : []).map(item => [String(item.variantId), Math.min(paidQuantity, 2)]));
      if (guarantee?.eligible && !reorderDetected && paid.some(item => String(item.product_id) === String(guarantee.productId))) {
        desired.set(String(guarantee.variantId), 1);
      }
      const updates = {};
      const found = new Set();
      for (const item of cart.items) {
        if (!isGift(item) && !isGuarantee(item) && !(reorderDetected && knownGiftIds.has(String(item.variant_id)))) continue;
        const id = String(item.variant_id);
        const quantity = found.has(id) ? 0 : desired.get(id) || 0;
        found.add(id);
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
      if (Object.keys(updates).length) {
        // Key-based updates preserve both line properties and cart attributes.
        await request('cart/update.js', { updates });
        continue;
      }
      const oldGuarantee = cart.items.find(item => isGuarantee(item) && item.properties?.Garantie === 'Réservée à la première commande, selon les conditions NUORO');
      if (oldGuarantee) {
        const properties = { ...oldGuarantee.properties };
        delete properties.Garantie;
        await request('cart/change.js', { id: oldGuarantee.key, quantity: oldGuarantee.quantity, properties });
        continue;
      }
      const items = [...desired].filter(([id]) => !found.has(id)).map(([id, quantity]) => ({
        id: Number(id), quantity, properties: giftProperties(id)
      }));
      if (items.length) {
        await request('cart/add.js', { items });
        continue;
      }
      if (!paidQuantity) reorderDetected = false;
      return cart;
    }
    throw new Error('Le panier a changé pendant sa mise à jour.');
  }

  async function updateMarkup(cart) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const sections = await request('?sections=cart-drawer,cart-icon-bubble');
    if (!sections['cart-drawer']) throw new Error('Impossible d’actualiser le panier.');
    drawer.classList.toggle('is-empty', cart.item_count === 0);
    if (typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections });
    } else {
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const source = html.querySelector('cart-drawer');
      if (!source) throw new Error('Impossible d’actualiser le panier.');
      drawer.innerHTML = source.innerHTML;
    }
    applyState();
  }

  function sync() {
    dirty = true;
    if (running) return running;
    syncing = true;
    failure = '';
    applyState();
    running = (async () => {
      do {
        dirty = false;
        let cart;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            cart = await reconcile();
            await updateMarkup(cart);
            break;
          } catch (error) {
            if (attempt || (error.status >= 400 && error.status < 500 && error.status !== 429)) throw error;
            await new Promise(resolve => setTimeout(resolve, 400));
          }
        }
        // Gift events are emitted only once the cart has converged, and awaited.
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-sync', cartData: cart });
      } while (dirty);
      return latestCart;
    })().catch(error => {
      failure = 'La mise à jour de vos cadeaux n’a pas abouti. Réessayez avant de continuer.';
      console.error('Gift synchronization failed:', error);
      throw error;
    }).finally(() => {
      syncing = false;
      running = null;
      applyState();
    });
    return running;
  }

  window.NuoroCartGuard = { acquire, sync, get busy() { return busy(); }, get error() { return failure; } };
  // Subscribe before the initial request so mutations during page load are queued.
  subscribe(PUB_SUB_EVENTS.cartUpdate, event => event.source === 'gift-sync' ? undefined : sync());
  sync().catch(() => {});
})();

